import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { RefListModel } from 'src/app/models/ref-list-model';
import { GenericPolluxResult } from '../../../models/generic-pollux-result';
import { AdminTabs } from '../../../navigation/enum';
import { NavigationService } from '../../../navigation/navigation.service';
import { CalculationModelOverview } from '../models/calculation-model-overview';
import { CalculationModelTemplate } from '../models/calculation-model-template';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';
import { EventBusService } from '../../../services/event-bus.service';

/**
 * Calc model service
 */
@Injectable({
  providedIn: 'root'
})
export class CalculationModelService {
  /**
   * Observable representing the search active state.
   */
  searchActive$: Observable<boolean>;

  /**
   * BehaviorSubject for the search active state.
   */
  private searchActive: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Observable representing the reset search state.
   */
  resetSearch$: Observable<boolean>;

  /**
   * BehaviorSubject for the reset search state.
   */
  private resetSearch: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Observable representing the edit mode state.
   */
  editMode$: Observable<boolean>;

  /**
   * BehaviorSubject for the edit mode state.
   */
  private editMode: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Observable representing the selected ID.
   */
  selectedId$: Observable<number>;

  /**
   * BehaviorSubject for the selected ID.
   */
  private selectedId: BehaviorSubject<number> = new BehaviorSubject(0);

  /**
   * Observable representing the status ID.
   */
  statusId$: Observable<number>;

  /**
   * BehaviorSubject for the status ID.
   */
  private statusId: BehaviorSubject<number> = new BehaviorSubject(0);

  /**
   * Resource for the calculation model.
   */
  private _calculationModelResource: string;


  /**
   * Resource for the common service.
   */
  private _commonResource: string;

  /**
   * Constructor
   * @param ais
   * @param httpClient
   * @param navigationService
   */
  constructor(private httpClient: HttpClient,
    private navigationService: NavigationService,
    private eventBus: EventBusService,
    private appServiceHelper: AppServiceHelperService) {
    this._calculationModelResource = this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'calculationmodeltemplate');
    this._commonResource = this.appServiceHelper.getServiceEndpoint('pollux.api', 'reflist');
    this.searchActive$ = this.searchActive.asObservable();
    this.resetSearch$ = this.resetSearch.asObservable();
    this.selectedId$ = this.selectedId.asObservable();
    this.statusId$ = this.statusId.asObservable();
  }

  //#region API

  /**
 * Retrieves a list of calculation models.
 * @returns An observable of CalculationModelOverview array.
 */
  getCalculationModels(): Observable<CalculationModelOverview[]> {
    return this.httpClient.get<CalculationModelOverview[]>(`${this._calculationModelResource}/list`);
  }

  /**
   * Retrieves a calculation model by ID.
   */
  getCalculationModel(oid: number): Observable<CalculationModelTemplate> {
    return this.httpClient.get<CalculationModelTemplate>(`${this._calculationModelResource}/single?calcModelTemplateId=${oid}`);
  }

  /**
   * Retrieves a reference list by name..
   */
  getRefList(name: string): Observable<RefListModel[]> {
    return this.httpClient.get<RefListModel[]>(`${this._commonResource}/pollux?reflistName=${name}`);
  }

  /**
   * Uploads a calculation model template.
   */
  uploadCalculationModelTemplate(data: any): Observable<any> {
    const oid = 0; // templates are loaded to VP 0
    const action = `import/v2/import/uploadfiles?referenceBoOid=${oid}&asynchronous=false`;
    const url = this.appServiceHelper.getServiceEndpoint('pollux.data.transformation.api', action);

    return this.httpClient.post(url, data);
  }

  /**
   * Prepares the mapping for a calculation model.
   */
  prepareMapping(guid: string): Observable<GenericPolluxResult> {
    return this.httpClient.post<GenericPolluxResult>(`${this._calculationModelResource}/mapping/prepare/${guid}`, {});
  }

  /**
   * Starts an edit session for a calculation model.
   * @returns An observable of a string.
   */
  startEditSession(): Observable<string> {
    return this.httpClient.post<string>(`${this._calculationModelResource}/mapping/session/start`, {});
  }

  /**
   * Ends an edit session for a calculation model.
   */
  endEditSession(guid: string): Observable<GenericPolluxResult> {
    return this.httpClient.post<GenericPolluxResult>(`${this._calculationModelResource}/mapping/session/end/${guid}`, {});
  }

  /**
   * Ensures the working file for a session and ID.
   */
  ensureWorkingFile(sessionGuid: string, id: number): Observable<GenericPolluxResult> {
    return this.httpClient.post<GenericPolluxResult>(`${this._calculationModelResource}/mapping/session/ensurefile/${sessionGuid}/${id}`, {});
  }

  /**
   * Upgrades the mapping version for a session and ID.
   */
  upgradeMappingVersion(sessionGuid: string, id: number): Observable<GenericPolluxResult> {
    return this.httpClient.post<GenericPolluxResult>(`${this._calculationModelResource}/mapping/upgrade/${sessionGuid}/${id}`, {});
  }

  /**
   * Uploads a session template.
   */
  uploadSessionTemplate(sessionGuid: string, id: number, dataUrl: string): Observable<GenericPolluxResult> {
    return this.httpClient.post<GenericPolluxResult>(`${this._calculationModelResource}/uploadfile/${sessionGuid}/${id}`, { dataUrl });
  }

  /**
   * Archives a calculation model by ID.
   */
  archive(id: number): Observable<GenericPolluxResult> {
    return this.httpClient.post<GenericPolluxResult>(`${this._calculationModelResource}/archive/${id}`, {});
  }

  /**
   * Creates a calculation model.
   */
  createCalculationModel(data: CalculationModelTemplate): Observable<any> {
    return this.httpClient.post<CalculationModelTemplate>(this._calculationModelResource, data);
  }

  /**
   * Updates a calculation model.
   */
  updateCalculationModel(sessionGuid: string, data: CalculationModelTemplate): Observable<any> {
    return this.httpClient.post<CalculationModelTemplate>(`${this._calculationModelResource}/update/${sessionGuid}`, data);
  }

  /**
   * Assigns a calculation model (check).
   * @param data The data to be assigned.
   * @returns An observable of GenericPolluxResult.
   */
  assignCalculationModelCheck(data: any): Observable<GenericPolluxResult> {
    return this.httpClient.post<GenericPolluxResult>(`${this._calculationModelResource}/assign/check`, data);
  }

  /**
   * Assigns a calculation model.
   * @param data The data to be assigned.
   * @returns An observable of GenericPolluxResult.
   */
  assignCalculationModel(data: any): Observable<GenericPolluxResult> {
    return this.httpClient.post<GenericPolluxResult>(`${this._calculationModelResource}/assign`, data);
  }

  /**
   * Performs an import preview.
   * @param data The data to be imported.
   * @returns An observable of any type.
   */
  importPreview(data: any): Observable<any> {
    const oid = 0; // templates are loaded to VP 0
    const action = `import/v2/import/prepare/uploaded?referenceBoOid=${oid}&autoFixErrors=false&asynchronous=false`;
    const url = this.appServiceHelper.getServiceEndpoint('pollux.import.api', action);

    return this.httpClient.post(url, data);
  }

  /**
   * Sets the search active state.
   * @param active The search active state.
   */
  setSearchActive(active: boolean) {
    this.searchActive.next(active);
  }

  /**
   * Triggers a reset search.
   */
  triggerResetSearch() {
    this.resetSearch.next(true);
  }

  /**
   * Triggers the selected ID.
   * @param id The selected ID.
   */
  triggerSelectedId(id: number): void {
    this.selectedId.next(id);
  }

  /**
   * Triggers the archive status.
   * @param statusId The archive status ID.
   */
  triggerArchiveStatus(statusId: number): void {
    this.statusId.next(statusId);
  }

  /**
   * Triggers the edit mode.
   */
  triggerEditMode(): void {
    setTimeout(() => {
      this.navigationService.setActiveAdminTab(AdminTabs.CalculationModelEdit);
    });
  }

  /**
   * Triggers the archive.
   */
  triggerArchive(): void {
    this.selectedId$.pipe(first()).subscribe(id => {
      if (id > 0) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: ()=> {
            this.archive(id).subscribe((gpr: GenericPolluxResult) => {
              if (gpr.success) {
                //force grid refresh
                this.navigationService.setActiveAdminTab(AdminTabs.CalculationModelNone);
                setTimeout(() => {
                  this.navigationService.setActiveAdminTab(AdminTabs.CalculationModel);
                });
              }
            });
          },
          cancel: () => {
            //do nothing: just to show Cancel button
          }
        }, 'archiveCalculationModelTemplateConfirmDialog');
      }
    });
  }

  //#endregion
}
